"use client";

import { useState } from "react";
import { AnimatePresence, motion, LayoutGroup } from "framer-motion";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";
import { IPricingCard, IPricingCardFeature, IPricingCardSlot } from "@/sanity/lib/definitions";
import { resolveI18nText } from "@/sanity/globals/I18n";
import { cn } from "@/lib/utils";
import { IDiscountSettings } from "@/sanity/templates";
import { PillTabs } from "@/components/pageComponents/global/PillTabs";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import { useCarouselApi } from "@/hooks/useCarouselApi";
import { Tooltip, TooltipTrigger, TooltipCustomContent, TooltipProvider } from "@/shadcn/ui/tooltip";

export type AnyPricingCard = IPricingCardSlot & {
  _key?: string;
};

const ease = [0.25, 0.1, 0.25, 1] as const;

function resolveCardDiscount(card: AnyPricingCard, discountSettings?: IDiscountSettings): number | undefined {
  // Page-level override takes priority over global discount
  if (typeof card.discountOverride === "number") {
    return card.discountOverride > 0 ? card.discountOverride : undefined;
  }
  if (!discountSettings) return undefined;
  const { assetType, accountType } = card;
  if (assetType === "futures" && accountType === "challenge")
    return discountSettings.futuresChallengeDiscountPercent || undefined;
  if (assetType === "futures" && accountType === "instant_funding")
    return discountSettings.futuresInstantFundingDiscountPercent || undefined;
  if (assetType === "cfd_crypto" && accountType === "challenge")
    return discountSettings.cryptoChallengeDiscountPercent || undefined;
  if (assetType === "cfd_crypto" && accountType === "instant_funding")
    return discountSettings.cryptoInstantFundingDiscountPercent || undefined;
  return undefined;
}

function applyDiscount(amount: string, discountPercent: number): string {
  const match = amount.match(/^([^\d]*)(\d[\d,.]*)(.*)$/);
  if (!match) return amount;
  const [, prefix, numStr, suffix] = match;
  const num = parseFloat(numStr.replace(/,/g, ""));
  if (isNaN(num)) return amount;
  const discounted = num * (1 - discountPercent / 100);
  const formatted = Number.isInteger(num) ? Math.round(discounted).toString() : discounted.toFixed(2);
  return `${prefix}${formatted}${suffix}`;
}

const CARD_THEME_STYLES = {
  light: {
    card: "bg-[rgba(255,255,255,1)] border-[rgba(0,0,0,0.1)] backdrop-blur-[0.25rem]",
    header: "bg-[#f6f4ef]",
    headerImageClass: "!w-full !h-full !object-cover mix-blend-luminosity opacity-40",
    headerImagePosition: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-[48%]",
    headerOverlay:
      "bg-[radial-gradient(circle_at_100%_62%,rgba(255,255,255,1)_0%,rgba(204,204,204,0.5)_48%,rgba(153,153,153,0)_100%)] opacity-90",
    amount: "text-black",
    badge: "bg-[#92ada6] text-white",
    statusWrap: "bg-white border-[rgba(0,0,0,0.17)]",
    statusIndicator: "bg-black",
    statusActiveText: "text-[#e4ecea]",
    statusInactiveText: "text-black/65",
  },
  sage: {
    card: "bg-white border-[rgba(0,0,0,0.1)]",
    header: "bg-[linear-gradient(142.244deg,#67A796_35.83%,#182824_205.58%)]",
    headerImageClass: "h-full w-full object-cover mix-blend-luminosity opacity-30",
    headerImagePosition: "inset-0",
    headerOverlay: "bg-[linear-gradient(128.546deg,rgba(156,171,167,1)_9.0193%,rgba(156,171,167,0)_79.622%)]",
    amount: "text-white",
    badge: "bg-[#92ada6] text-white",
    statusWrap: "bg-white border-[rgba(0,0,0,0.17)]",
    statusIndicator: "bg-black",
    statusActiveText: "text-white",
    statusInactiveText: "text-black/65",
  },
  dark: {
    card: "bg-white border-[rgba(0,0,0,0.1)]",
    header: "bg-black",
    headerImageClass: "h-[49.25rem] w-[66rem] object-cover mix-blend-luminosity opacity-40",
    headerImagePosition: "left-[-6.25rem] top-[-13.625rem]",
    headerOverlay: "",
    amount: "text-white",
    badge: "bg-[#42af92] text-white",
    statusWrap: "bg-[#323232] border-[rgba(255,255,255,0.17)]",
    statusIndicator: "bg-white",
    statusActiveText: "text-black",
    statusInactiveText: "text-white/60",
  },
} as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="-1 -1 22 22"
      fill="none"
      className={cn("shrink-0", className)}>
      <path
        d="M10 7H10.01M9 10H10V14H11M10 1C17.2 1 19 2.8 19 10C19 17.2 17.2 19 10 19C2.8 19 1 17.2 1 10C1 2.8 2.8 1 10 1Z"
        stroke="#BABABA"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FeatureTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip
        open={open}
        onOpenChange={setOpen}>
        <TooltipTrigger
          asChild
          className="h-[2.5rem] w-[2.25rem]">
          <span
            className="flex shrink-0 cursor-default items-center"
            onClick={(e) => {
              e.preventDefault();
              setOpen((o) => !o);
            }}>
            <InfoIcon className="h-full w-full shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipCustomContent
          side="top"
          className="max-w-[24rem] rounded-[0.375rem] bg-black px-[1.25rem] py-[0.75rem] font-geist text-[1.75rem] leading-[1.4] text-white">
          {text}
        </TooltipCustomContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-[1rem] w-[1.5rem] shrink-0 text-black/55"
      viewBox="0 0 12 8"
      fill="none">
      <path
        d="M1 4L4.4 7L11 1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
      />
    </svg>
  );
}

function CardStatusTabs({
  challengeLabel,
  fundedLabel,
  theme,
  cardId,
  selectedStatus,
  onStatusChange,
}: {
  challengeLabel?: string;
  fundedLabel?: string;
  theme: keyof typeof CARD_THEME_STYLES;
  cardId: string;
  selectedStatus: "challenge" | "funded";
  onStatusChange: (v: "challenge" | "funded") => void;
}) {
  const styles = CARD_THEME_STYLES[theme];
  const options = [
    { value: "challenge" as const, label: challengeLabel || "Challenge" },
    { value: "funded" as const, label: fundedLabel || "Funded" },
  ];
  return (
    <PillTabs
      options={options}
      value={selectedStatus}
      onChange={onStatusChange}
      layoutId={`pricing-card-status-${cardId}`}
      className={cn("absolute right-[3.5rem] top-[2.25rem] border font-geist p-[0.5rem]", styles.statusWrap)}
      buttonClassName="px-[1.5rem] py-[1rem] text-[1.75rem] tracking-[-0.035rem]"
      activeClassName={styles.statusActiveText}
      inactiveClassName={styles.statusInactiveText}
      indicatorClassName={styles.statusIndicator}
    />
  );
}

function PricingCard({
  card,
  challengeLabel,
  fundedLabel,
  index,
  language,
  discountSettings,
}: {
  card: AnyPricingCard;
  challengeLabel?: string;
  fundedLabel?: string;
  index: number;
  language?: string;
  discountSettings?: IDiscountSettings;
}) {
  const discountPercent = resolveCardDiscount(card, discountSettings);
  const [selectedStatus, setSelectedStatus] = useState<"challenge" | "funded">("challenge");

  const theme = card.theme ?? "light";
  const styles = CARD_THEME_STYLES[theme];
  const c = card as IPricingCard;

  const resolvedBadge = resolveI18nText(card.badge, c.badgeI18n, language);
  const resolvedFeeSuffix = resolveI18nText(card.feeSuffix, c.feeSuffixI18n, language);
  const resolvedFundedFeeSuffix = resolveI18nText(card.fundedFeeSuffix, c.fundedFeeSuffixI18n, language);

  const isFunded = selectedStatus === "funded";
  const activeFeatures = isFunded && card.fundedFeatures?.length ? card.fundedFeatures : card.features;
  const activeFeeAmount = isFunded && card.fundedFeeAmount ? card.fundedFeeAmount : card.feeAmount;
  const activeFeeSuffix = isFunded ? resolvedFundedFeeSuffix : resolvedFeeSuffix;

  const buttonData = card.cta
    ? {
        ...card.cta,
        type: card.cta.type ?? "default",
        size: card.cta.size ?? "sm",
        shape: card.cta.shape ?? "square",
      }
    : null;

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.8, ease, delay: index * 0.08 }}>
      {card.highlight && (
        <>
          <div className="pointer-events-none absolute top-0 left-0 z-10 flex w-fit -translate-y-full items-center justify-center rounded-tl-[0.25rem] rounded-tr-[0.25rem] bg-[#1aa87f] px-[3rem] py-[0.5rem]">
            <p className="mb-0 text-[1.625rem] leading-[1.2] tracking-[-0.04rem] whitespace-nowrap text-white">
              New Offer
            </p>
          </div>
          <div className="pointer-events-none absolute inset-0 z-20 rounded-[0.25rem] border border-[#1aa87f]" />
        </>
      )}
      <article
        className={cn("relative flex min-h-[83.75rem] flex-col overflow-hidden rounded-[0.25rem] border", styles.card)}>
        <div className={cn("relative h-[22rem] overflow-hidden border-b border-black/10", styles.header)}>
          {card.headerImage && (
            <div
              className={cn("absolute pointer-events-none", styles.headerImagePosition)}
              aria-hidden="true">
              <GroqImage
                src={card.headerImage}
                alt=""
                className={styles.headerImageClass}
              />
            </div>
          )}

          {styles.headerOverlay && (
            <span
              aria-hidden="true"
              className={cn("absolute inset-0 pointer-events-none", styles.headerOverlay)}
            />
          )}

          {resolvedBadge && (
            <span
              className={cn(
                "absolute font-geist left-[4rem] top-[3.25rem] rounded-[0.25rem] px-[1rem] py-[0.5rem] text-[2rem] leading-[1.2] tracking-[-0.04rem] shadow-[inset_0_0.5rem_1.0375rem_rgba(255,255,255,0.37)]",
                styles.badge,
              )}>
              {resolvedBadge}
            </span>
          )}

          {card.accountType !== "instant_funding" && (
            <CardStatusTabs
              challengeLabel={challengeLabel}
              fundedLabel={fundedLabel}
              theme={theme}
              cardId={card._key ?? `${card.amount ?? "card"}-${index}`}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
            />
          )}

          {card.amount && (
            <p
              className={cn(
                "absolute bottom-[2.75rem] left-[4rem] mb-0 text-[6.75rem] font-normal leading-[1.2] tracking-[-0.4725rem]",
                styles.amount,
              )}>
              {card.amount}
            </p>
          )}
        </div>

        <div className="flex flex-1 flex-col px-[4rem] pt-[3.375rem] pb-[4rem]">
          <AnimatePresence
            mode="wait"
            initial={false}>
            <motion.div
              key={selectedStatus}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -6, transition: { duration: 0.18, ease } }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.055 } },
              }}
              className="flex flex-1 flex-col">
              <div className="flex flex-col">
                {activeFeatures?.map((feature) => {
                  const f = feature as IPricingCardFeature;
                  const label = resolveI18nText(feature.label, f?.labelI18n, language);
                  const value = resolveI18nText(feature.value, f?.valueI18n, language);
                  return (
                    <motion.div
                      key={feature._key ?? `${feature.label}-${feature.value}`}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.28, ease },
                        },
                      }}
                      className="flex items-center justify-between gap-[2rem] border-b border-black/10 py-[1.75rem]">
                      <div className="flex min-w-0 items-center gap-[1rem]">
                        <CheckIcon />
                        <p className="mb-0 min-w-0 text-[2rem] leading-none font-normal text-black/62">{label}</p>
                        {f?.description && (
                          <FeatureTooltip text={resolveI18nText(f.description, f.descriptionI18n, language) ?? ""} />
                        )}
                      </div>
                      <p className="mb-0 text-right text-[2rem] leading-[1.5] font-medium text-black/80">{value}</p>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.28, ease },
                  },
                }}
                className="mt-auto pt-[5.5rem]">
                {(activeFeeAmount || activeFeeSuffix) && (
                  <div className="mb-[2.75rem] flex flex-col gap-[0.25rem]">
                    {activeFeeAmount && discountPercent ? (
                      <>
                        <div className="flex items-center gap-[1.25rem]">
                          <p className="mb-0 text-[2.5rem] leading-[1.35] font-medium tracking-[-0.07rem] text-black/35 line-through">
                            {activeFeeAmount}
                          </p>
                          <span className="rounded-[0.25rem] bg-[#1aa87f] px-[1rem] py-[0.375rem] text-[1.625rem] leading-none font-medium tracking-[-0.03rem] text-white">
                            -{discountPercent}%
                          </span>
                        </div>
                        <div className="flex items-end gap-[0.75rem]">
                          <p className="mb-0 text-[4.106rem] leading-[1.35] font-medium tracking-[-0.123rem] text-black">
                            {applyDiscount(activeFeeAmount, discountPercent)}
                          </p>
                          {activeFeeSuffix && (
                            <p className="mb-[0.5rem] text-[1.75rem] leading-[1.35] font-medium tracking-[-0.035rem] text-black/50">
                              {activeFeeSuffix}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-end gap-[0.75rem]">
                        {activeFeeAmount && (
                          <p className="mb-0 text-[4.106rem] leading-[1.35] font-medium tracking-[-0.123rem] text-black">
                            {activeFeeAmount}
                          </p>
                        )}
                        {activeFeeSuffix && (
                          <p className="mb-[0.5rem] text-[1.75rem] leading-[1.35] font-medium tracking-[-0.035rem] text-black/50">
                            {activeFeeSuffix}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {buttonData && (
                  <div className="[&_.button]:!min-h-[5.875rem] [&_.button]:!w-full [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem] [&_.button_.button-text]:!text-[2rem] [&_.button_.button-text-hover]:!text-[2rem] [&_.cta-wrapper]:!block [&_.cta-wrapper]:!w-full [&_.cta-wrapper]:sm:!w-full">
                    <CTAButton data={buttonData} />
                  </div>
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </article>
    </motion.div>
  );
}

export interface PricingCardsGridProps {
  cards: AnyPricingCard[];
  assetPrimaryLabel?: string;
  assetSecondaryLabel?: string;
  defaultAssetTab?: "primary" | "secondary";
  challengeAccountsLabel?: string;
  instantFundingLabel?: string;
  challengeLabel?: string;
  fundedLabel?: string;
  language?: string;
  className?: string;
  discountSettings?: IDiscountSettings;
}

export default function PricingCardsGrid({
  cards,
  assetPrimaryLabel,
  assetSecondaryLabel,
  defaultAssetTab,
  challengeAccountsLabel,
  instantFundingLabel,
  challengeLabel,
  fundedLabel,
  language,
  className,
  discountSettings,
}: PricingCardsGridProps) {
  const [selectedAsset, setSelectedAsset] = useState<"futures" | "cfd_crypto">(
    defaultAssetTab === "secondary" ? "futures" : "cfd_crypto",
  );
  const [selectedAccountType, setSelectedAccountType] = useState<"challenge" | "instant_funding">("challenge");
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } = useCarouselApi();

  const hasAssetFilter = cards.some((c) => c.assetType);
  const hasAccountTypeFilter = cards.some((c) => c.accountType);

  const parseAmount = (amount?: string) => {
    if (!amount) return 0;
    const n = parseFloat(amount.replace(/[^0-9.]/g, ""));
    if (/k/i.test(amount)) return n * 1000;
    if (/m/i.test(amount)) return n * 1_000_000;
    return n;
  };

  const filteredCards = cards
    .filter((c) => {
      if (hasAssetFilter && c.assetType && c.assetType !== selectedAsset) return false;
      if (hasAccountTypeFilter && c.accountType && c.accountType !== selectedAccountType) return false;
      return true;
    })
    .sort((a, b) => parseAmount(a.amount) - parseAmount(b.amount));

  const assetOptions = [
    ...(assetPrimaryLabel ? [{ value: "cfd_crypto" as const, label: assetPrimaryLabel }] : []),
    ...(assetSecondaryLabel ? [{ value: "futures" as const, label: assetSecondaryLabel }] : []),
  ];

  const accountTypeOptions = hasAccountTypeFilter
    ? [
        {
          value: "challenge" as const,
          label: challengeAccountsLabel || "Challenge Accounts",
        },
        {
          value: "instant_funding" as const,
          label: instantFundingLabel || "Instant Funding",
        },
      ]
    : [];

  return (
    <div className={cn("flex flex-col items-center w-full", className)}>
      {(assetOptions.length > 0 || accountTypeOptions.length > 0) && (
        <div className="mt-[3.62rem] flex flex-col items-center gap-[2rem] lg:mt-[5.5rem] lg:flex-row">
          {assetOptions.length > 0 && (
            <PillTabs
              options={assetOptions}
              value={selectedAsset}
              onChange={setSelectedAsset}
              layoutId="pricing-cards-asset-tab"
              className="font-geist"
            />
          )}
          {accountTypeOptions.length > 0 && (
            <PillTabs
              options={accountTypeOptions}
              value={selectedAccountType}
              onChange={setSelectedAccountType}
              layoutId="pricing-cards-account-type-tab"
              className="font-geist"
            />
          )}
        </div>
      )}

      {/* Mobile carousel */}
      <div className="mt-[4rem] flex w-full flex-col gap-[4rem] md:hidden">
        <Carousel
          setApi={setApi}
          opts={{ align: "center", loop: false }}
          className="w-full">
          <CarouselContent className="gap-[3rem]">
            <LayoutGroup id="carousel-pricing">
              {filteredCards.map((card, index) => (
                <CarouselItem
                  key={card._key ?? `${card.amount}-${index}`}
                  className="basis-full pt-[4rem] pl-0">
                  <PricingCard
                    card={card}
                    challengeLabel={challengeLabel}
                    fundedLabel={fundedLabel}
                    index={index}
                    language={language}
                    discountSettings={discountSettings}
                  />
                </CarouselItem>
              ))}
            </LayoutGroup>
          </CarouselContent>
        </Carousel>

        {count > 1 && (
          <div className="mx-auto flex w-[min(50rem,100%)] items-center gap-[2rem]">
            <button
              type="button"
              onClick={handlePrev}
              disabled={isFirst}
              aria-label="Previous"
              className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black/10 transition-opacity hover:opacity-70 disabled:opacity-30">
              <ArrowIcon className="h-[2.125rem] w-[2.125rem] rotate-180 text-black" />
            </button>

            <div className="h-[0.5rem] flex-1 overflow-hidden rounded-[3rem] bg-black/10">
              <motion.div
                className="h-full rounded-full bg-black"
                animate={{
                  width: `${count > 1 ? ((current - 1) / (count - 1)) * 100 : 100}%`,
                }}
                transition={{ duration: 0.3, ease }}
              />
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={isLast}
              aria-label="Next"
              className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity hover:opacity-80 disabled:opacity-30">
              <ArrowIcon className="h-[2.125rem] w-[2.125rem] text-white" />
            </button>
          </div>
        )}
      </div>

      {/* Desktop grid */}
      <div className="mt-[8rem] hidden w-full gap-[3rem] md:grid md:grid-cols-2 lg:mt-[5rem] xl:grid-cols-3">
        {filteredCards.map((card, index) => (
          <PricingCard
            key={card._key ?? `${card.amount}-${index}`}
            card={card}
            challengeLabel={challengeLabel}
            fundedLabel={fundedLabel}
            index={index}
            language={language}
            discountSettings={discountSettings}
          />
        ))}
      </div>
    </div>
  );
}
